/**
 * TRANSITIONAL FILE (Phase 8.7C+)
 * Still used in current routing: /ups-admin/accounts
 *
 * Realtime model update:
 * - DO NOT import/use socket directly in components anymore.
 * - Realtime listeners are centralized in: frontend/src/services/socket.js
 * - This page stays fresh by refetching when statsRefreshKey changes.
 */

// frontend/src/features/ups/AccountDashboard.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';

// If you already have a selector exported, use it.
// Otherwise read directly from state (shown below).
// import { selectFilters } from '../ui/filtersSlice';

export default function AccountDashboard() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for filtering, sorting, and search
  const [filter, setFilter] = useState('agency');
  const [sortBy, setSortBy] = useState('username');
  const [searchTerm, setSearchTerm] = useState('');

  // ✅ Central invalidation signal (bumped by services/socket.js)
  const statsRefreshKey = useSelector(
    (state) => state.ui?.filters?.statsRefreshKey
  );

  // Fetch account list (initial + on invalidation)
  useEffect(() => {
    let mounted = true;

    async function fetchAccounts() {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get('/ups-admin/accounts');
        if (mounted) setAccounts(response.data);
      } catch (err) {
        if (mounted) {
          setError(err.response?.data?.error || 'Error fetching accounts');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchAccounts();

    return () => {
      mounted = false;
    };
  }, [statsRefreshKey]);

  // Filter, search, and sort
  const filtered = accounts.filter((acc) =>
    filter === 'ups' ? acc.role.startsWith('ups') : acc.role.startsWith('agency')
  );

  const searched = filtered.filter((acc) => {
    const q = searchTerm.toLowerCase();
    return (
      acc.username.toLowerCase().includes(q) ||
      acc.email.toLowerCase().includes(q) ||
      (acc.agency_name || '').toLowerCase().includes(q)
    );
  });

  const sorted = [...searched].sort((a, b) => {
    if (sortBy === 'agency_name') {
      return (a.agency_name || '').localeCompare(b.agency_name || '');
    }
    return a[sortBy].localeCompare(b[sortBy]);
  });

  // Actions
  const toggleBlock = async (id, blocked) => {
    try {
      const res = await axios.put('/ups-admin/accounts/block', {
        userId: id,
        block: !blocked,
      });
      // Optimistic local update; socket invalidation will also refetch for safety
      setAccounts((prev) => prev.map((u) => (u.id === id ? res.data : u)));
    } catch {
      alert('Error updating block status');
    }
  };

  const deleteAccount = async (id) => {
    try {
      await axios.delete(`/ups-admin/accounts/${id}`);
      // Optimistic local update; socket invalidation will also refetch for safety
      setAccounts((prev) => prev.filter((u) => u.id !== id));
    } catch {
      alert('Error deleting account');
    }
  };

  if (loading) return <p className="text-center text-gray-500">Loading accounts...</p>;
  if (error) return <p className="text-center text-red-600">{error}</p>;

  return (
    <div className="overflow-x-auto my-8">
      <h3 className="text-2xl font-bold mb-4 text-gray-800">Account Dashboard</h3>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setFilter('agency')}
          className={`px-4 py-2 rounded transition ${
            filter === 'agency'
              ? 'bg-blue-600 text-white'
              : 'bg-blue-500 text-white hover:bg-blue-700'
          }`}
        >
          Agency Accounts
        </button>
        <button
          onClick={() => setFilter('ups')}
          className={`px-4 py-2 rounded transition ${
            filter === 'ups'
              ? 'bg-blue-600 text-white'
              : 'bg-blue-500 text-white hover:bg-blue-700'
          }`}
        >
          UPS Accounts
        </button>
      </div>

      {/* Controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <div className="flex items-center space-x-2">
          <label className="text-gray-700">Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="username">Username</option>
            <option value="role">Role</option>
            {filter === 'agency' && <option value="agency_name">Agency Name</option>}
          </select>
        </div>
        <div>
          <input
            type="text"
            placeholder="Search accounts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      {/* Table */}
      <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            {[
              'ID',
              'Username',
              'Email',
              'Role',
              ...(filter === 'agency' ? ['Agency Name'] : []),
              'Blocked',
              'Actions',
            ].map((header) => (
              <th
                key={header}
                className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {sorted.map((acc) => (
            <tr key={acc.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-base text-gray-900">{acc.id}</td>
              <td className="px-6 py-4 whitespace-nowrap text-base text-gray-900">{acc.username}</td>
              <td className="px-6 py-4 whitespace-nowrap text-base text-gray-900">{acc.email}</td>
              <td className="px-6 py-4 whitespace-nowrap text-base text-gray-900">{acc.role}</td>
              {filter === 'agency' && (
                <td className="px-6 py-4 whitespace-nowrap text-base text-gray-900">
                  {acc.agency_name || 'N/A'}
                </td>
              )}
              <td className="px-6 py-4 whitespace-nowrap text-base text-gray-900">
                {acc.blocked ? 'Yes' : 'No'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {acc.role !== 'ups_admin' ? (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => toggleBlock(acc.id, acc.blocked)}
                      className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                    >
                      {acc.blocked ? 'Unblock' : 'Block'}
                    </button>
                    <button
                      onClick={() => deleteAccount(acc.id)}
                      className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition"
                    >
                      Delete
                    </button>
                  </div>
                ) : (
                  <span className="text-gray-400 italic">Protected</span>
                )}
              </td>
            </tr>
          ))}
          {!sorted.length && (
            <tr>
              <td className="px-6 py-6 text-sm text-gray-500" colSpan={filter === 'agency' ? 7 : 6}>
                No accounts found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
